import { PostgrestError } from "@supabase/supabase-js";
import { Notification } from "providers/NotifierProvider";
import { supabase } from "supabase/client";
import { Database } from "supabase/types";
import logSentryError from "utils/logSentryError";

type DbGroupSlugs = Database["public"]["Tables"]["group_slugs"];
export type GroupSlugsRow = DbGroupSlugs["Row"];
export type GroupSlugsInsert = DbGroupSlugs["Insert"];
export type GroupSlugsUpdate = DbGroupSlugs["Update"];

class GroupSlugsApi {
  showNotification?: (notification: Notification) => void;

  constructor(showNotification?: (notification: Notification) => void) {
    this.showNotification = showNotification;
  }

  // FIXME: Only group admins should be able to get the slug
  async getGroupSlug(groupId: number): Promise<{
    slug: GroupSlugsRow["slug"] | null;
    error: PostgrestError | null;
  }> {
    const { data, error } = await supabase.from("group_slugs").select("slug").eq("group_id", groupId).single();

    if (error && error.code !== "PGRST116") {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to get invite link.",
          severity: "error",
        });
      }

      logSentryError(error, "GROUP_SLUGS_API: getGroupSlug");
      return { slug: null, error };
    }

    return { slug: data?.slug ?? null, error: null };
  }

  async generateGroupSlug(groupId: number): Promise<{ slug: string | null; error: PostgrestError | null }> {
    const { data, error } = await supabase.rpc("generate_group_slug", {
      group_id_param: groupId,
    });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to generate invite link.",
          severity: "error",
        });
      }

      logSentryError(error, "GROUP_SLUGS_API: generateGroupSlug");
      return { slug: null, error };
    }

    return { slug: data, error: null };
  }

  async joinGroupViaLink(slug: string): Promise<{ groupName: string | null; error: PostgrestError | null }> {
    const { data, error } = await supabase.rpc("join_group_via_link", {
      slug_param: slug,
    });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to join group.",
          severity: "error",
        });
      }

      logSentryError(error, "GROUP_SLUGS_API: joinGroupViaLink");
      return { groupName: null, error };
    }

    return { groupName: data, error: null };
  }
}

export default GroupSlugsApi;
